/**
 * Turning the audit trail into something a person reads.
 *
 * The deal ledger was rendering `event_type` verbatim and `JSON.stringify` of
 * the details blob — so a reviewer saw
 *
 *     ingestion_thread_update_applied
 *     {"ingestionId":"INQ-987D3797","loanId":"JAB-7D1DED1C","applied":{...}}
 *
 * which is a database row, not a record of what happened.
 *
 * Worse than the formatting: most of what appeared there was not a deal event
 * at all. Every authenticated API call is audited as `request`, and those rows
 * carry the loan id in their query string, so "somebody opened this page" was
 * being listed alongside real decisions. Filtering those out removes most of
 * the noise on its own.
 *
 * Nothing here invents a description. An event with no phrase falls back to its
 * own name in sentence case, which is honest and still readable — better than
 * a confident label that misdescribes what happened.
 */

import { splitDeskStamp } from '@/utils/dates'

/** Audit rows that are not deal events. A page view is not a stage event. */
const NOT_A_DEAL_EVENT = new Set([
  'request',
  'login_success',
  'login_failed',
  'login_passkey_success',
])

/** event_type -> what a person would say happened. */
const PHRASES: Record<string, string> = {
  // Intake and parsing
  ingestion_archived: 'Message archived',
  ingestion_broker_linked: 'Broker linked',
  ingestion_deal_name_resolved: 'Deal name confirmed',
  ingestion_reingest_resolved: 'Re-ingest reconciled',
  ingestion_reparse_applied: 'Re-parse applied',
  ingestion_rerun_measured: 'Re-parse measured',
  ingestion_review_decision: 'Datapoint decided',
  ingestion_review_decision_withdrawn: 'Datapoint decision withdrawn',
  ingestion_smart_review_applied: 'Smart Review applied',
  data_review_decision: 'Data Intelligence Review decision',
  ingestion_thread_update_applied: 'Later message applied to the deal',
  ingestion_thread_update_kept: "Deal's value kept over a later message",
  ingestion_adhoc_lines_applied: 'Workbook lines applied',
  loan_removed_not_a_deal: 'Removed from pipeline — not a deal',
  model_template_draft_from_intake: 'Model template drafted',
  portfolio_materialized: 'Portfolio constituents created',
  portfolio_properties_adopted: 'Deals rolled up into the portfolio',
  portfolio_property_adopted: 'Added to a portfolio',
  portfolio_properties_released: 'Properties removed from the portfolio',
  portfolio_property_released: 'Removed from a portfolio',
  // A portfolio's per-property underwriting models
  uw_model_added: 'Underwriting model added',
  uw_model_assigned: 'Underwriting model filed under a property',
  uw_model_moved: 'Underwriting model moved to the other side',
  uw_model_relabeled: 'Underwriting model relabelled',
  uw_model_basis_set: 'Roll-up model chosen',
  uw_model_removed: 'Underwriting model removed',
  // The deal itself
  loan_updated: 'Deal updated',
  loan_field_approved: 'Field approved',
  loan_killed: 'Deal killed',
  loan_marked_dead: 'Deal marked dead',
  loan_archived: 'Deal archived',
  loan_restored: 'Deal restored',
  loan_reopened: 'Deal reopened',
  loan_funded_with_override: 'Funded with an override',
  loan_narratives_updated: 'Narrative updated',
  loan_calculation_overridden: 'Calculation overridden',
  loan_calculation_override_withdrawn: 'Calculation override withdrawn',
  loan_derivation_conflict: 'Derivation conflict raised',
  // The same words the deal's Audit Trail uses for these events.
  deal_reconciled: 'Reconciliation pass',
  loan_backfilled_from_record: 'Values carried from the intake record',
  loan_created: 'Deal created',
  loan_party_added: 'Party added',
  loan_party_updated: 'Party edited',
  loan_party_removed: 'Party removed',
  loan_document_uploaded: 'Document uploaded',
  loan_document_deleted: 'Document deleted',
  loan_requirement_added: 'Checklist item added',
  loan_requirement_updated: 'Checklist item changed',
  loan_requirement_removed: 'Checklist item removed',
  // CRM
  broker_merged: 'Brokers merged',
  broker_firm_merged: 'Brokerage firms merged',
  // Communications the desk logs by hand
  call: 'Call',
  email: 'Email',
  meeting: 'Meeting',
  note: 'Note',
  task: 'Task',
}

/** What each verb of the Data Intelligence Review reads as in the ledger. */
const REVIEW_VERB: Record<string, string> = {
  confirm: 'confirmed',
  correct: 'corrected',
  unavailable: 'marked unavailable',
  withdraw: 'withdrew the decision on',
  accept: 'added',
  dismiss: 'dismissed',
}

/** Communication kinds — what belongs in a communications table. */
export const COMMUNICATION_KINDS = new Set(['call', 'email', 'meeting', 'note', 'task'])

export function isDealEvent(eventType: string | null | undefined): boolean {
  const kind = (eventType || '').trim()
  return Boolean(kind) && !NOT_A_DEAL_EVENT.has(kind)
}

/** "ingestion_thread_update_applied" -> "Later message applied to the deal". */
export function humanizeEvent(eventType: string | null | undefined): string {
  const kind = (eventType || '').trim()
  if (!kind) return 'Event'
  if (PHRASES[kind]) return PHRASES[kind]
  // Honest fallback: the event's own name, made readable. A new event type
  // reads a little bare rather than being mislabelled by a guess.
  const words = kind.replace(/[_-]+/g, ' ').trim()
  return words.charAt(0).toUpperCase() + words.slice(1)
}

/** Keys, compared without case or underscores.
 *
 *  The API camelCases every response INCLUDING the details blob, so the same
 *  event reaches this code as `field_name` from the database and `fieldName`
 *  through the service. Matching one spelling silently matches nothing in the
 *  running app — which is precisely what a local fixture written in the other
 *  spelling will not reveal.
 */
function normalizeKeys(details: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {}
  for (const [k, v] of Object.entries(details)) {
    out[k.replace(/_/g, '').toLowerCase()] = v
  }
  return out
}

function pick(details: Record<string, unknown>, keys: string[]): string | null {
  for (const k of keys) {
    const v = details[k.replace(/_/g, '').toLowerCase()]
    if (typeof v === 'string' && v.trim()) return v.trim()
    if (typeof v === 'number') return String(v)
  }
  return null
}

/**
 * A short, readable statement of WHAT the event carried.
 *
 * Reads the few keys that mean something to a person and ignores the rest —
 * ids, correlation keys and query strings are for the audit tab, not for a
 * ledger someone scans. Returns null when there is nothing worth saying, which
 * is better than a line of punctuation.
 */
export function describeEvent(details: unknown): string | null {
  if (!details || typeof details !== 'object' || Array.isArray(details)) return null
  const d = normalizeKeys(details as Record<string, unknown>)

  const reason = pick(d, ['reason', 'kill_reason_label', 'note'])
  const field = pick(d, ['field', 'field_name'])
  // `property_name` arrives as `property`: the transform's key map renames it.
  const name = pick(d, ['new_name', 'property_name', 'property', 'name'])

  const applied = d.applied
  if (applied && typeof applied === 'object' && !Array.isArray(applied)) {
    const fields = Object.keys(applied as Record<string, unknown>)
    if (fields.length) {
      const shown = fields.slice(0, 4).join(', ')
      const more = fields.length > 4 ? ` +${fields.length - 4} more` : ''
      return `${fields.length} field${fields.length === 1 ? '' : 's'}: ${shown}${more}`
    }
  }

  // A person declined a later email's figure: which fields, and why.
  const kept = d.kept
  if (kept && typeof kept === 'object' && !Array.isArray(kept)) {
    const fields = Object.keys(kept as Record<string, unknown>)
    if (fields.length) {
      const why = pick(d, ['rationale'])
      return `kept ${fields.slice(0, 4).join(', ')}${fields.length > 4 ? ` +${fields.length - 4} more` : ''}${why ? ` — ${why}` : ''}`
    }
  }

  const changed = d.changed
  if (typeof changed === 'number' && changed >= 0) {
    return `${changed} field${changed === 1 ? '' : 's'} differed`
  }

  // A Data Intelligence Review decision names what the person did to the
  // field, and says so when it overruled somebody else's answer.
  const verb = pick(d, ['verb'])
  const subject = verb && field ? `${REVIEW_VERB[verb] ?? verb} ${field}` : field
  const overruled = d.override === true ? "overruled a person's decision" : null
  const parts = [name, subject, reason, overruled].filter(Boolean) as string[]
  return parts.length ? parts.join(' — ') : null
}

/**
 * Split a stored timestamp into the date and time columns the desk reads, on the
 * desk's (New York) clock. The API sends most timestamps naive and every naive
 * timestamp is UTC: `new Date(value)` read one as the browser's local time, so
 * the Stage Event Ledger and Latest Communications printed UTC wall time.
 */
export function splitStamp(value: string | null | undefined): { date: string; time: string } {
  if (!value) return { date: '—', time: '—' }
  return splitDeskStamp(value) ?? { date: value, time: '' }
}
